import moment from 'moment-timezone';
import { formatCurrency, formatDateMoment, TranslateText } from '@/helpers/utils/siteUtils';
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DEFAULT_SITE_LANG,
  CUSTOMER_DETAILS_CONSTANTS,
  ORDER_CONFIRMATION_NUMBER,
} from '../../../../../constants/constant';
import { DEFAULT_COUNTRY } from '../../customer-detail/constants';
import { TrackingInfo } from '../../customer-detail/types';
import { PageTexts } from '../i18n';
import { OrderDataType, OrderDetailsType, OrderItemsType, OrderNotesType, OrderSummaryInfo, Payment } from '../types';

const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);

export const getOrderDetails = (orderData: OrderDataType) => {
  const orderDetails = {
    orderId: orderData?.orderId,
    customer: `${orderData?.BillingAddress?.PersonInfo?.firstName ?? ''} ${
      orderData?.BillingAddress?.PersonInfo?.lastName ?? ''
    }`,
    division: orderData?.OrderInfo?.divisionCode,
    origin: orderData?.OrderInfo?.medium,
    status: orderData?.OrderInfo?.statusDescription,
    submitted: formatDateMoment(orderData?.OrderInfo?.submittedOn, DATE_TIME_FORMAT),
    agent: orderData?.OrderInfo?.submittedBy,
    confirmationThirdPartyOrderId: orderData?.ExternalOrderInfo?.externalOrderId,
    originalNumOfLines: orderData?.OrderInfo?.originalNumOfLines,
    emailAddress: orderData?.OrderInfo?.emailAddress,
  };

  return orderDetails;
};

export const getOrderDetailsWithFields = (orderDetails: OrderDetailsType) => {
  const accountData = [
    { field: CUSTOMER_DETAILS_CONSTANTS.ORDER_ID, value: orderDetails?.orderId, mask: true },
    { field: CUSTOMER_DETAILS_CONSTANTS.CUSTOMER, value: orderDetails?.customer, mask: true },
    { field: CUSTOMER_DETAILS_CONSTANTS.DIVISION, value: orderDetails?.division },
    { field: CUSTOMER_DETAILS_CONSTANTS.ORIGIN, value: orderDetails?.origin },
  ];
  const isMailOrder = orderDetails?.origin === 'Mail';

  const accountAddress = [
    { field: CUSTOMER_DETAILS_CONSTANTS.STATUS, value: orderDetails?.status },
    { field: CUSTOMER_DETAILS_CONSTANTS.SUBMITTED, value: orderDetails?.submitted },
    { field: CUSTOMER_DETAILS_CONSTANTS.AGENT, value: orderDetails?.agent, mask: true },
    {
      field: CUSTOMER_DETAILS_CONSTANTS.CONFIRMATION_THIRD_PARTY_ORDER_ID,
      value: orderDetails?.confirmationThirdPartyOrderId,
      key: ORDER_CONFIRMATION_NUMBER,
      mask: true,
    },
  ];

  if (isMailOrder) {
    delete accountAddress[3];
  }
  return { accountData, accountAddress };
};

export const getOrderItems = (orderData: OrderDataType) => {
  const orderItems =
    orderData?.Recipients?.[0]?.LineItems?.map((lineItem) => {
      return {
        lineItemInfo: lineItem?.LineItemInfo,
        productInfo: lineItem?.Product?.ProductInfo,
        shippedOn: lineItem?.LineItemInfo?.shippedOn,
        shipQuantity: lineItem?.LineItemInfo?.shipQuantity,
        orderQuantity: lineItem?.LineItemInfo?.orderQuantity,
        backorderQuantity: lineItem?.LineItemInfo?.backorderQuantity,
        warrantyStatus: lineItem?.LineItemInfo?.warrantyStatus,
        fulfillmentMethod: lineItem?.LineItemInfo?.fulfillmentMethod,
        itemPriceInfo: lineItem?.ItemPriceInfo,
        lineSequence: lineItem?.LineItemInfo?.lineSequence,
        postSubmissionInfo: lineItem?.PostSubmissionInfo,
        image: lineItem?.imageUrl,
      };
    }) ?? [];

  return orderItems;
};

export const getShippingAndBillingDetails = (orderData: OrderDataType) => {
  const shippingAddress = orderData?.Recipients?.[0]?.Address?.LocationInfo;
  const billingAddress = orderData?.BillingAddress?.LocationInfo;
  const emailAddress = orderData?.Recipients?.[0]?.RecipientInfo?.emailAddress;
  const personInfo = orderData?.Recipients?.[0]?.Address?.PersonInfo;
  const orderInfo = orderData?.OrderInfo ?? {};

  return { shippingAddress, billingAddress, emailAddress, personInfo, orderInfo };
};

export const getOrderNotes = (orderData: OrderDataType) => {
  return orderData?.Notes || [];
};

export function transformNotes(notes: OrderNotesType) {
  return (
    notes?.map((note: any) => {
      const { note: noteText, createdBy, createdOn } = note;
      const [agentName, dateTime] =
        createdBy && createdOn ? [`${createdBy}`, formatDateMoment(createdOn, DATE_TIME_FORMAT)] : ['', ''];
      return { agent: agentName, dateTime, notes: noteText };
    }) ?? []
  );
}

export const getLineItems = (orderItems: OrderItemsType, orderId: string) => {
  const lineItems = orderItems?.map((order) => ({
    itemDescription: order?.productInfo?.productName,
    itemId: order?.productInfo?.vSku,
    color: order?.productInfo?.color,
    itemUnitPrice: order?.itemPriceInfo?.unitPrice,
    quantity: order?.shipQuantity,
    lineShippedDate: order?.shippedOn,
    image: order?.productInfo?.imageUrl ?? '',
    pdpUrl: order?.productInfo?.pdpUrl ?? '',
    status: order?.lineItemInfo?.statusDescription,
    postSubmissionIndo: order?.postSubmissionInfo,
    itemNumber: order?.productInfo?.legacyStyle,
    lineSequenceNumber: order?.lineSequence,
    isShippingInsurancePolicy: order?.lineItemInfo?.isShippingInsurancePolicy,
  }));
  return { orderId, lineItems };
};

const getInvoiceLineItems = (InvoiceItems: any, lineItems: any, lineItemsWithImages: any) => {
  const InvoiceLineItems: any = InvoiceItems?.map((InvoiceLineItem: any) => {
    const InvoiceItem = lineItems?.find(
      (item: any) => item?.LineItemInfo?.lineSequence === InvoiceLineItem?.lineSequence,
    );
    const InvoiceImageItem = lineItemsWithImages?.lineItems?.find(
      (item: any) => item?.lineSequenceNumber === InvoiceLineItem?.lineSequence,
    );
    return {
      ...InvoiceLineItem,
      itemDescription: InvoiceImageItem?.itemDescription
        ? InvoiceImageItem?.itemDescription
        : InvoiceItem?.itemDescription,
      image: InvoiceImageItem?.image ?? '',
      pdpUrl: InvoiceImageItem?.pdpUrl ?? '',
      trackingDetails: InvoiceItem?.TrackingDetails,
      color: InvoiceItem?.Product?.ProductInfo?.color,
    };
  });
  const ItemImages = InvoiceLineItems?.map((item: any) => {
    if (item?.image) {
      return {
        image: item?.image,
        lineId: item?.lineSequence,
      };
    }
  })?.filter((item: any) => item?.image);
  return { InvoiceLineItems, ItemImages };
};

export const transformOrderDetails = (orderData: OrderDataType) => {
  const orderDetails = getOrderDetails(orderData);
  const orderItems = getOrderItems(orderData);
  const shippingAndBillingDetails = getShippingAndBillingDetails(orderData);
  const transformedOrderNotes = transformNotes(orderData?.Notes);
  const RecipientAddresses = orderData?.Recipients?.[0]?.Address;
  const lineItems = getLineItems(orderItems, orderDetails?.orderId);
  const customerId = orderData?.OrderInfo?.omsCustomerId;
  const isOrderReleasable = orderData?.PostSubmissionInfo?.AvailableActions?.isReleaseable;
  const Invoices = orderData?.PricingInfo?.Invoices ?? [];
  const trackingNumbers = orderData?.Recipients?.[0]?.Shipments;
  const status: TrackingInfo[] = [];
  trackingNumbers?.forEach((items) => {
    const trackingDetails = items?.TrackingDetails ?? [];
    trackingDetails?.forEach((data: any) => {
      const info = data?.TrackingInfo ?? {};
      status.push({ trackingCode: info?.trackingCode, trackingLink: info?.trackingLink });
    });
  });
  const RecipientLineItems = orderData?.Recipients?.[0]?.LineItems ?? [];
  const Shipments = orderData?.Recipients?.[0]?.Shipments ?? [];
  const lineItemsWithShipmentDetails = mapTrackingDetailsToLineItems(RecipientLineItems, Shipments);
  const lineItemsWithAdjustments = mapTrackingDetailsToLineItems(RecipientLineItems, Shipments)?.filter(
    (items: any) => items?.LineItemInfo?.statusDescription === 'Adjustment',
  );
  const mappedAdjustedItems = mapAdjustmentLineItems(lineItemsWithAdjustments ?? []);
  const InvoiceData = {
    Invoice: Invoices?.map((Invoice) => {
      const { InvoiceLineItems, ItemImages } = getInvoiceLineItems(
        Invoice?.InvoiceLines,
        lineItemsWithShipmentDetails,
        lineItems,
      );
      return {
        invoiceNumber: Invoice?.invoiceId,
        date: formatDateMoment(Invoice?.createdOn, DATE_FORMAT),
        numberOfItems: Invoice?.numberOfLines,
        price: formatCurrency(Invoice?.subtotal),
        tax: formatCurrency(Invoice?.taxAmt),
        shipping: formatCurrency(Invoice?.freightAmt),
        totalAmount: formatCurrency(Invoice?.totalAmt),
        trackId: status,
        lineItems: InvoiceLineItems,
        ItemImages: ItemImages,
      };
    }),
  };

  const shipments = orderData?.Recipients?.[0]?.Shipments ?? [];
  const returnTrackingDetails = shipments?.map((items) => items?.ReturnTrackingDetails)?.filter((items) => items) ?? [];
  const payRecords = getPayRecords(orderData?.Payments);

  const Invoiced: OrderSummaryInfo = orderData?.PricingInfo?.Invoiced ?? {};
  const EstimatedTotal: OrderSummaryInfo = orderData?.PricingInfo?.EstimatedTotal ?? {};
  const Backordered: OrderSummaryInfo = orderData?.PricingInfo?.Backordered ?? {};
  const ReadyToShip: OrderSummaryInfo = orderData?.PricingInfo?.ReadyToShip ?? {};
  const orderTotalDetails = getOrderTotalDetails(Invoiced, EstimatedTotal, Backordered, ReadyToShip);
  const orderSummaryInfo = {
    ...orderData?.orderSummaryInfo,
    subTotal: (
      parseFloat(orderData?.orderSummaryInfo?.subTotal ?? '0') +
      parseFloat(orderData?.orderSummaryInfo?.returnInfo?.returnRefund ?? '0')
    ).toString(),
  };
  const orderBalance = orderData?.PricingInfo?.EstimatedTotal?.balance ?? 0;
  const canSendTracer = orderData?.PostSubmissionInfo?.AvailableActions?.canSendTracer;
  const canSendReturnLabel = orderData?.PostSubmissionInfo?.AvailableActions?.canSendPpLabel;
  const showOrderAdjustment = orderData?.PostSubmissionInfo?.AvailableActions?.isAppeasable;
  const isOrderCancelable = orderData?.PostSubmissionInfo?.AvailableActions?.isCancelable;
  const showAdvanceShippingOption = orderData?.PostSubmissionInfo?.AvailableActions?.isSignatureRequiredAdjustable;
  const isSignatureRequired = orderData?.OrderInfo?.isSignatureRequired;
  const isOrderHoldable = orderData?.PostSubmissionInfo?.AvailableActions?.isHoldable;
  const isAddressAdjustable = orderData?.PostSubmissionInfo?.AvailableActions?.isShipAddressAdjustable;
  const isShippingMethodAdjustable = orderData?.PostSubmissionInfo?.AvailableActions?.canAdjustPaymentMethod;
  const billingAddress = getBillingAddressInfo(orderData?.BillingAddress);
  const paymentMethods = orderData?.Payments?.[0];
  const isOrderAddressEditable = isOrderAddressAdjustable(
    orderData?.ExternalOrderInfo?.externalSource,
    orderData?.Payments,
  );
  const giftMessage = orderData?.Recipients?.[0]?.RecipientInfo?.giftMessage;
  const GroupedItems = orderData?.OrderGroupItems ?? [];
  return {
    orderDetails,
    orderItems,
    shippingAndBillingDetails,
    orderNotes: transformedOrderNotes,
    RecipientAddresses,
    lineItems,
    customerId,
    isOrderReleasable,
    Invoices,
    InvoiceData,
    lineItemsWithShipmentDetails: lineItemsWithShipmentDetails,
    lineItemsWithAdjustments,
    returnTrackingDetails,
    payRecords,
    orderTotalDetails,
    RecipientLineItems,
    canSendTracer,
    showOrderAdjustment,
    isOrderCancelable,
    GroupedItems,
    showAdvanceShippingOption,
    isSignatureRequired,
    isAddressAdjustable,
    isOrderAddressEditable,
    EstimatedTotal,
    isShippingMethodAdjustable,
    billingAddress,
    paymentMethods,
    orderSummaryInfo,
    canSendReturnLabel,
    orderBalance,
    adjustedItems: mappedAdjustedItems,
    giftMessage,
    isOrderHoldable,
  };
};

const mapAdjustmentLineItems = (lineItems: any) => {
  if (lineItems?.length === 0) return [];

  const adjustedItems = lineItems
    ?.map((item: any) => {
      return {
        lineSequence: item?.LineItemInfo?.lineSequence,
        productInfo: item?.Product?.ProductInfo,
        quantity: item?.LineItemInfo?.orderQuantity,
        itemPriceInfo: item?.ItemPriceInfo,
        productId: item?.Product?.ProductInfo?.vSku,
        showProtectionPlan: false,
        showPersonalizationLink: false,
        showCancelItemLink: item?.PostSubmissionInfo?.AvailableActions?.isCancelable,
        backorderQuantity: item?.LineItemInfo?.backorderQuantity,
        shipQuantity: item?.LineItemInfo?.shipQuantity,
        orderQuantity: item?.LineItemInfo?.orderQuantity,
        statusCode: item?.LineItemInfo?.statusCode,
        statusDesc: item?.LineItemInfo?.statusDescription,
        postSubmissionInfo: item?.PostSubmissionInfo,
        fulfillmentMethod: item?.LineItemInfo?.fulfillmentMethod,
        imageUrl: item?.Product?.ProductInfo?.imageUrl,
        pdpUrl: item?.Product?.ProductInfo?.pdpUrl,
        protectionPlanInfo: {},
        personalizationInfo: {},
        writeAReviewInfo: {},
        cancelItemInfo: {},
        date: item?.LineItemInfo?.lineAddedOn,
      };
    })
    ?.reverse();
  return adjustedItems;
};

export const mapTrackingDetailsToLineItems = (lineItems: any, shipments: any) => {
  return (
    lineItems?.map((lineItem: any) => {
      const { ItemShipments } = lineItem;
      const trackingDetails = ItemShipments?.flatMap((shipment: any) => {
        const { shipmentId } = shipment;
        const shipmentInfo = shipments?.find((s: any) => s?.ShipmentInfo?.shipmentId === shipmentId);
        return shipmentInfo ? shipmentInfo?.TrackingDetails : [];
      });

      return {
        ...lineItem,
        TrackingDetails: trackingDetails ?? [],
      };
    }) ?? []
  );
};

export const getCancelReason = (reasonId: string) => {
  switch (reasonId) {
    case '1':
      return Texts.operatorChange;
    case '2':
      return Texts.customerReqBackOrder;
    case '3':
      return Texts.customerReqChangeMind;
    default:
      return Texts.operatorChange;
  }
};

const getPayRecords = (Payments: Payment[]) => {
  return (
    Payments?.map((items) => {
      return {
        createdOn: items?.PaymentInfo?.createdOn ?? '',
        description: items?.PaymentInfo?.statusDescription ?? '',
        paymentType: items?.PaymentInfo?.paymentType ?? '',
        PaymentDetail: items?.PaymentDetail ?? {},
        amount: items?.PaymentInfo?.amount ?? '',
        transactionStatus: items?.PaymentInfo?.statusDescription ?? '',
        termsDescription: items?.PaymentInfo?.termsDescription ?? '',
        isPaymentRecord: items?.PaymentInfo?.isPaymentRecord,
      };
    })?.filter((items) => items?.isPaymentRecord) ?? []
  );
};

const getOrderTotalDetails = (
  Invoiced: OrderSummaryInfo,
  EstimatedTotal: OrderSummaryInfo,
  Backordered: OrderSummaryInfo,
  ReadyToShip: OrderSummaryInfo,
) => {
  const orderTotalDetails = [
    {
      displayHeader: 'Subtotal:',
      invoice: Invoiced?.merchandiseAmt ?? '',
      backorder: Backordered?.merchandiseAmt ?? '',
      ready_to_ship: ReadyToShip?.merchandiseAmt ?? '',
      est_order_total: EstimatedTotal?.merchandiseAmt ?? '',
    },
    {
      displayHeader: 'Shipping:',
      invoice: Invoiced?.shippingAmt ?? '',
      backorder: Backordered?.shippingAmt ?? '',
      ready_to_ship: ReadyToShip?.shippingAmt ?? '',
      est_order_total: EstimatedTotal?.shippingAmt ?? '',
      toolTipIcon: true,
    },
    {
      displayHeader: 'Shipping Surcharge:',
      invoice: Invoiced?.shippingSurchargeAmt ?? '',
      backorder: Backordered?.shippingSurchargeAmt ?? '',
      ready_to_ship: ReadyToShip?.shippingSurchargeAmt ?? '',
      est_order_total: EstimatedTotal?.shippingSurchargeAmt ?? '',
      toolTipIcon: true,
    },
    {
      displayHeader: 'Sales Tax:',
      invoice: Invoiced?.taxAmt ?? '',
      backorder: Backordered?.taxAmt ?? '',
      ready_to_ship: ReadyToShip?.taxAmt ?? '',
      est_order_total: EstimatedTotal?.taxAmt ?? '',
      toolTipIcon: true,
    },
    {
      displayHeader: 'Payments:',
      invoice: Invoiced?.payments ?? '',
      backorder: '',
      ready_to_ship: '',
      est_order_total: EstimatedTotal?.payments ?? '',
    },
    {
      displayHeader: 'Balance:',
      invoice: Invoiced?.balance ?? '',
      backorder: '',
      ready_to_ship: '',
      est_order_total: EstimatedTotal?.balance ?? '',
    },
    {
      displayHeader: 'Order Total:',
      invoice: Invoiced?.totalAmt ?? '',
      backorder: Backordered?.totalAmt ?? '',
      ready_to_ship: ReadyToShip?.totalAmt ?? '',
      est_order_total: EstimatedTotal?.totalAmt ?? '',
      isBold: true,
    },
  ];
  return orderTotalDetails;
};

export const getProductStatus = (statusCode: string) => {
  switch (statusCode) {
    case 'ADJ':
      return { status: 'Adjustment', color: 'black' };
    case 'BAK':
      return { status: 'Backorder', color: 'deepBlue' };
    case 'CAN':
      return { status: 'Cancelled', color: 'scarletRed' };
    case 'RET':
      return { status: 'Returned', color: 'black' };
    case 'SHP':
      return { status: 'Shipped', color: 'forestGreen' };
    case 'PRO':
      return { status: 'Processing', color: 'forestGreen' };
    default:
      return { status: '', color: '' };
  }
};

export const isOrderAddressAdjustable = (orderSource: string, payments: Payment[]) => {
  let isOrderAddressAdjust = true;
  if (orderSource === 'Amazon') {
    isOrderAddressAdjust = false;
  } else if (orderSource === 'EBay') {
    isOrderAddressAdjust = false;
  } else {
    payments?.forEach((payment) => {
      if (payment?.PaymentInfo?.paymentType === 'PayPal') {
        return (isOrderAddressAdjust = false);
      }
    });
  }
  return isOrderAddressAdjust;
};

export const getBillingAddressInfo = (billingAddress: any) => {
  const LocationInfo = billingAddress?.LocationInfo ?? {};
  const firstName = billingAddress?.PersonInfo?.firstName ?? '';
  const lastName = billingAddress?.PersonInfo?.lastName ?? '';
  const companyName = billingAddress?.PersonInfo?.companyName ?? '';
  const addressId = billingAddress?.HeaderInfo?.addressId;
  return {
    fullName: `${firstName} ${lastName}`,
    firstName,
    lastName,
    streetName: LocationInfo?.line1,
    additionalAddressInfo: LocationInfo?.line2,
    city: LocationInfo?.city,
    state: LocationInfo?.state,
    postalCode: LocationInfo?.zip,
    country: DEFAULT_COUNTRY,
    addressId: addressId,
    phone: LocationInfo?.phoneNumber,
    companyName,
  };
};

export function convertUTCtoCST(utcDateString: string) {
  return moment.utc(utcDateString).tz('America/Chicago').format('MM/DD/YYYY hh:mm:ss A');
}

export const formatNotesDate = (dateString: string): string => {
  if (!dateString) return '';

  const [datePart, timePart] = dateString.split('T');
  if (!datePart || !timePart) return dateString;

  const [year, month, day] = datePart.split('-');
  const [hour, minute, second] = timePart.split(':');

  // Convert to 12-hour format
  const hourInt = parseInt(hour, 10);
  const formattedHour = (hourInt % 12 || 12).toString();
  const amPm = hourInt >= 12 ? 'PM' : 'AM';

  return `${month}/${day}/${year} ${formattedHour}:${minute}:${second.slice(0, 2)} ${amPm}`;
};

export const addFromOrderParam = (url: string): string => {
  if (!url) return url;
  const hasParams = url.includes('?');
  return `${url}${hasParams ? '&' : '?'}fromOrder=true`;
};

export const processNotes = (data: any[]) => {
  const groups: any[] = [];
  let currentGroup: any[] = [];
  let firstOrphanGroup: any[] | null = null;

  // Handle first row edge case
  if (data?.length && !data[0]?.agent && !data[0]?.dateTime) {
    firstOrphanGroup = [data[0]];
    data = data?.slice(1); // remove it from processing
  }

  data?.forEach((item) => {
    const isNewNote = item?.agent && item?.dateTime;

    if (isNewNote) {
      if (currentGroup?.length) {
        groups?.push(currentGroup);
      }
      currentGroup = [item];
    } else {
      currentGroup?.push(item);
    }
  });

  if (currentGroup?.length) {
    groups?.push(currentGroup);
  }

  // Sort groups (descending)
  groups?.sort((a, b) => {
    const dateA = new Date(a[0].dateTime).getTime();
    const dateB = new Date(b[0].dateTime).getTime();
    return dateB - dateA;
  });

  // Flatten sorted groups
  let result = groups?.flat();

  // Append orphan note at the end
  if (firstOrphanGroup) {
    result = [...result, ...firstOrphanGroup];
  }

  return result;
};
